/**
 * Connection Health Store — the evidence-based health rail (connection-truth).
 *
 * One source of truth for "can this client actually hear the server":
 * `healthy | degraded | down`, derived from ABSENCE of expected traffic rather
 * than from error events (errors are unreliable narrators during network
 * failure; the server's 15s ping cadence makes silence itself measurable).
 *
 * Evidence inputs (pushed imperatively by connectionStore's socket wiring):
 *  - last-inbound-packet clock — the engine emits 'packet' on EVERY inbound
 *    frame incl. pongs, so a healthy connection is never silent >15s. Silence
 *    >20s ⇒ degraded, >35s ⇒ down (~40s before Socket.IO's own 75s timeout).
 *  - app-level heartbeat RTT (CONNECTION_HEARTBEAT ack) — sustained slow acks
 *    degrade even while packets flow: the "slow internet" signal.
 *  - browser offline event ⇒ down immediately.
 *  - socket disconnect of an ESTABLISHED connection ⇒ down once the drop
 *    outlives DISCONNECT_GRACE_MS. socket.io re-dials most drops within
 *    seconds, and presenting every one was a capsule per re-dial (founder
 *    09-01: "you're offline · reconnecting" flashing on a fresh load). The
 *    grace arms its own ender, so a drop that outlives it presents at exactly
 *    the grace, never at the next evaluator tick. Nothing shown inside the
 *    grace is untrue — the link is being re-dialed — and "reconnecting…" still
 *    appears when the drop outlives it.
 *
 * Consumers render PRESENTATION from this rail (Reconnecting line, capsule,
 * header vocabulary). It never mutates task phase — when the viewer goes
 * blind we change the presentation, never the intent (spec §2).
 */
import { create } from "zustand";
import { posthog } from "@/lib/posthog";
import { isPageVisible, subscribePageVisibility } from "@/lib/page-visibility";
import { ANALYTICS_EVENTS } from "@bash/shared";

export type ConnectionHealth = "healthy" | "degraded" | "down";

export const DEGRADED_SILENCE_MS = 20_000;
export const DOWN_SILENCE_MS = 35_000;
export const DEGRADED_RTT_MS = 3_000;
export const HEARTBEAT_INTERVAL_MS = 10_000;
export const HEARTBEAT_ACK_TIMEOUT_MS = 5_000;
/** A lost socket presents as down only once the drop outlives this. */
export const DISCONNECT_GRACE_MS = 4_000;
/** Evaluator cadence — cheap wall-clock comparison, no I/O. */
const EVALUATE_INTERVAL_MS = 5_000;

export function deriveHealth(input: {
  browserOffline: boolean;
  socketConnected: boolean;
  /** Has a socket connection EVER been established this session? A page still
   *  making its FIRST connection has not lost anything — "connecting" must
   *  not present as "down" (founder 08-28: a reloaded tab said "no
   *  connection, reconnecting" while the network was fine). Until the first
   *  connect, the silence clock alone decides. */
  hadConnection: boolean;
  /** ms since an ESTABLISHED socket dropped; null = connected, never
   *  connected, or no stamp. Inside DISCONNECT_GRACE_MS the drop does not
   *  present as down (socket.io is re-dialing). A lost socket with NO stamp is
   *  down at once: the stamp only ever delays the word, never hides it. */
  msSinceDisconnect: number | null;
  /** ms since the engine's last inbound frame; null = no packet seen yet. */
  msSinceInbound: number | null;
  /** last heartbeat round-trip; null = no beat yet. Infinity = missed ack. */
  lastRttMs: number | null;
}): ConnectionHealth {
  if (input.browserOffline) return "down";
  if (!input.socketConnected && input.hadConnection) {
    const grace = input.msSinceDisconnect;
    if (grace === null || grace >= DISCONNECT_GRACE_MS) return "down";
    // Inside the grace: fall through — silence and rtt still speak.
  }
  if (input.msSinceInbound !== null && input.msSinceInbound > DOWN_SILENCE_MS)
    return "down";
  if (
    input.msSinceInbound !== null &&
    input.msSinceInbound > DEGRADED_SILENCE_MS
  )
    return "degraded";
  if (input.lastRttMs !== null && input.lastRttMs > DEGRADED_RTT_MS)
    return "degraded";
  return "healthy";
}

interface ConnectionHealthStore {
  health: ConnectionHealth;
  /** The BROWSER's own word (online/offline events). Copy needs it to tell
   *  "You're offline" (browser says so — true) from "Can't reach Vestra"
   *  (browser online, link down — blaming their wifi would be untrue). */
  offline: boolean;
}

export const useConnectionHealthStore = create<ConnectionHealthStore>()(() => ({
  health: "healthy",
  offline: false,
}));

// Evidence lives module-level, not in the store: packets arrive at wire rate
// and a per-frame zustand set() would re-render subscribers constantly. Only
// the derived `health` transition is reactive.
let lastInboundAt: number | null = null;
let lastRttMs: number | null = null;
let browserOffline = false;
let socketConnected = false;
let hadConnection = false;
/** When the established socket dropped; null while connected. */
let disconnectedAt: number | null = null;
/** The grace's ender: re-evaluates at exactly DISCONNECT_GRACE_MS after the
 *  drop so 'down' never waits for the evaluator tick. Lives and dies with the
 *  stamp — cleared only where the stamp is. */
let graceTimer: ReturnType<typeof setTimeout> | null = null;
let evaluatorTimer: ReturnType<typeof setInterval> | null = null;
/** Lives and dies with the evaluator: the tab-return re-base below. */
let unsubscribeVisibility: (() => void) | null = null;
/** The visibility we last acted on, so only the hidden → visible EDGE counts.
 *  `subscribePageVisibility` also fires on window `focus`, which is every click
 *  back into a tab that never left — and re-basing there would hand a dead link
 *  a fresh DOWN_SILENCE_MS on every click. */
let wasVisible = true;

function clearGrace(): void {
  disconnectedAt = null;
  if (graceTimer) {
    clearTimeout(graceTimer);
    graceTimer = null;
  }
}

function evaluate(): void {
  const next = deriveHealth({
    browserOffline,
    socketConnected,
    hadConnection,
    msSinceDisconnect:
      disconnectedAt === null ? null : Date.now() - disconnectedAt,
    msSinceInbound: lastInboundAt === null ? null : Date.now() - lastInboundAt,
    lastRttMs,
  });
  const prev = useConnectionHealthStore.getState().health;
  if (next === prev) return;
  useConnectionHealthStore.setState({ health: next });
  posthog.capture(ANALYTICS_EVENTS.CONNECTION_HEALTH_CHANGED, {
    from: prev,
    to: next,
    ms_since_inbound:
      lastInboundAt === null ? null : Date.now() - lastInboundAt,
    rtt_ms: lastRttMs === Infinity ? null : lastRttMs,
  });
}

/** Engine saw an inbound frame — the strongest liveness evidence there is. */
export function noteInboundPacket(): void {
  lastInboundAt = Date.now();
  // Snap back to healthy without waiting for the next tick — recovery should
  // feel instant even though decay may take a full interval to register.
  evaluate();
}

export function noteRtt(ms: number): void {
  lastRttMs = ms;
  evaluate();
}

/** Heartbeat ack timed out — treated as worst-case RTT evidence. */
export function noteHeartbeatMiss(): void {
  lastRttMs = Infinity;
  evaluate();
}

export function setBrowserOffline(offline: boolean): void {
  browserOffline = offline;
  if (useConnectionHealthStore.getState().offline !== offline) {
    useConnectionHealthStore.setState({ offline });
  }
  evaluate();
}

export function setSocketConnected(connected: boolean): void {
  socketConnected = connected;
  if (connected) {
    hadConnection = true;
    clearGrace();
    // A fresh connection starts with a clean slate — stale silence/RTT from
    // the dead transport must not damn the new one.
    lastInboundAt = Date.now();
    lastRttMs = null;
  } else if (hadConnection && disconnectedAt === null) {
    // An established socket dropped. Stamp it ONCE — the socket teardown
    // re-reports the same drop and must not restart the grace — and arm the
    // ender that presents 'down' the moment the grace is spent.
    disconnectedAt = Date.now();
    graceTimer = setTimeout(() => {
      graceTimer = null;
      evaluate();
    }, DISCONNECT_GRACE_MS);
  }
  evaluate();
}

export function startHealthEvaluator(): void {
  if (evaluatorTimer) return;
  // The silence clock starts when we start listening: a first connection that
  // never lands still walks healthy → degraded → down on the same rail as a
  // lost one, instead of flashing "down" before it ever had a chance.
  if (lastInboundAt === null) lastInboundAt = Date.now();
  evaluatorTimer = setInterval(evaluate, EVALUATE_INTERVAL_MS);
  // Silence accrued while the tab was hidden is not evidence: the browser
  // paused us, not the server (founder 09-10: "Can't reach Vestra ·
  // reconnecting…" on every return to an idle tab). On the RETURN the clocks
  // re-base — the same clean slate a fresh connection gets — and a link that
  // is really dead is still caught DOWN_SILENCE_MS later. Nothing is hidden,
  // only the first paint.
  //
  // Only the edge re-bases. A tab that never went away has accrued real
  // silence, and letting a `focus` click forgive it would blunt the dead-link
  // detector at exactly the moment someone is looking at it.
  wasVisible = isPageVisible();
  unsubscribeVisibility = subscribePageVisibility((visible) => {
    const returned = visible && !wasVisible;
    wasVisible = visible;
    if (!returned) return;
    lastInboundAt = Date.now();
    lastRttMs = null;
    // ...and the DROP, for the same reason. A hidden tab is usually throttled
    // rather than frozen, so the browser still delivers the close the server
    // sent at its 75 s ping timeout — and `deriveHealth` short-circuits on the
    // socket BEFORE it ever reaches the silence clock re-based above. Left
    // alone, a minutes-old stamp makes `msSinceDisconnect` dwarf the grace and
    // paints "Can't reach Vestra" the instant the tab is looked at, whatever
    // the silence clock says.
    //
    // A drop we were not awake to see is not a drop the user experienced.
    // Re-stamp it to the RETURN and re-arm its ender, so socket.io gets the
    // same DISCONNECT_GRACE_MS to re-dial that it would get in a foreground
    // tab. A link that is genuinely dead still presents `down` one grace later.
    if (disconnectedAt !== null) {
      disconnectedAt = Date.now();
      if (graceTimer) clearTimeout(graceTimer);
      graceTimer = setTimeout(() => {
        graceTimer = null;
        evaluate();
      }, DISCONNECT_GRACE_MS);
    }
    evaluate();
  });
}

/** Forget everything this session's socket taught us. Called when the socket
 *  is DESTROYED (logout / identity switch): the next session must start as a
 *  never-connected page, not inherit a dead one's evidence. */
export function resetHealthEvidence(): void {
  lastInboundAt = null;
  lastRttMs = null;
  socketConnected = false;
  hadConnection = false;
  clearGrace();
  evaluate();
}

export function stopHealthEvaluator(): void {
  if (evaluatorTimer) {
    clearInterval(evaluatorTimer);
    evaluatorTimer = null;
  }
  unsubscribeVisibility?.();
  unsubscribeVisibility = null;
  wasVisible = true;
}
